import React, { Component } from "react";
import { Row, Col, Typography, notification, Select, Tag } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Form,
  FormItem,
  InputNumberItem,
  ButtonItem,
  SelectItem,
  SelectPlantillasEvaluacionItem,
  DatePickerItem,
} from "../../../../Components/Items";
import axios from "axios";
import { connect } from "react-redux";
import moment from "moment";

const { Text, Paragraph } = Typography;

const FULL = { labelCol: { span: 24 }, wrapperCol: { span: 24 } };

const FRECUENCIAS = [
  { value: "mensual", label: "Mensual" },
  { value: "trimestral", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
  { value: "anual", label: "Anual" },
];

// Programa qué plantillas de evaluación se aplican a este proveedor, cada cuándo y a quién notificar
class ProgramacionEvaluacionesProveedor extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    guardando: false,
    tipoProveedorId: null,
    usuariosEmpresa: [],
    programacionEvaluaciones: [],
    plantillaEvaluacionEntregaId: null,
  };

  componentDidMount() {
    this.cargarUsuarios();
    this.cargarDatos();
  }

  cargarUsuarios = () => {
    axios
      .post(
        "usuarios/selectUsuariosEmpresa",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken },
      )
      .then((res) => {
        if (Array.isArray(res.data)) this.setState({ usuariosEmpresa: res.data });
      })
      .catch(() => {});
  };

  cargarDatos = () => {
    if (!this.props.proveedorId) return;
    this.setState({ loading: true });
    axios
      .post(
        "evaluacionesProveedor/obtenerProgramacion",
        { proveedorId: this.props.proveedorId },
        { headers: this.props.headersToken },
      )
      .then((res) => {
        if (res.data.success === 2) {
          const programacionEvaluaciones = (res.data.proveedor.programacionEvaluaciones || []).map(
            (p) => ({
              ...p,
              plantillaId: p.plantillaId?._id || p.plantillaId,
              plantillaNombreMostrado: p.plantillaId?.nombre,
              ultimaGenerada: p.ultimaGenerada ? moment(p.ultimaGenerada) : undefined,
              inicioProgramado: p.inicioProgramado ? moment(p.inicioProgramado) : undefined,
            }),
          );
          this.setState(
            {
              tipoProveedorId: res.data.proveedor.tipoProveedorId,
              programacionEvaluaciones,
              plantillaEvaluacionEntregaId: res.data.proveedor.plantillaEvaluacionEntregaId || null,
            },
            () => this.formRef.current?.setFieldsValue({ programacionEvaluaciones }),
          );
        } else {
          notification.error({ message: res.data.message });
        }
      })
      .catch(() => notification.error({ message: "Error al cargar la programación." }))
      .finally(() => this.setState({ loading: false }));
  };

  guardar = async () => {
    try {
      const values = await this.formRef.current.validateFields();
      this.setState({ guardando: true });
      axios
        .post(
          "evaluacionesProveedor/guardarProgramacion",
          {
            proveedorId: this.props.proveedorId,
            programacionEvaluaciones: values.programacionEvaluaciones || [],
            plantillaEvaluacionEntregaId: this.state.plantillaEvaluacionEntregaId || null,
          },
          { headers: this.props.headersToken },
        )
        .then((res) => {
          if (res.data.success === 2) {
            notification.success({ message: res.data.message });
            this.cargarDatos();
          } else {
            notification.error({ message: res.data.message });
          }
        })
        .catch(() => notification.error({ message: "Error al guardar la programación." }))
        .finally(() => this.setState({ guardando: false }));
    } catch (errorInfo) {
      console.error("Faltan campos de programación:", errorInfo);
      notification.warning({ message: "Completa los campos marcados en rojo (plantilla, día y frecuencia)." });
    }
  };

  render() {
    const { tipoProveedorId, usuariosEmpresa, programacionEvaluaciones } = this.state;

    if (!this.props.proveedorId) {
      return (
        <Paragraph type="secondary">
          Guarda primero el proveedor para poder programarle evaluaciones.
        </Paragraph>
      );
    }

    return (
      <div>
        <div
          style={{
            border: "1px solid #ebedf0",
            borderRadius: 6,
            padding: "12px 14px",
            marginBottom: 20,
            background: "#fafafa",
          }}
        >
          <Text strong>Plantilla de referencia para tiempos de entrega</Text>
          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
            Al llenar una evaluación con esta plantilla para este proveedor, se mostrará como dato
            informativo el historial de fechas de entrega (estimada vs. real) del periodo evaluado.
            Es un campo único, no una lista.
          </Paragraph>
          <SelectPlantillasEvaluacionItem
            allowClear
            headersToken={this.props.headersToken}
            empresaId={this.props.empresaId}
            tipoProveedorId={tipoProveedorId}
            value={this.state.plantillaEvaluacionEntregaId}
            onChange={(value) => this.setState({ plantillaEvaluacionEntregaId: value ?? null })}
            style={{ width: 400 }}
          />
        </div>

        <Paragraph type="secondary" style={{ marginTop: -4 }}>
          Programa qué plantilla se le aplica a este proveedor, cada cuándo y a quién avisar. La
          "próxima fecha" se calcula sola a partir de la frecuencia: anclada a la "Última
          evaluación realizada" si ya le habían hecho esta evaluación antes, o a "Forzar primera
          evaluación para" si quieres elegir en qué mes debe caer la primera. Si dejas ambas
          vacías, se cuenta desde hoy.
        </Paragraph>

        <Form
          ref={this.formRef}
          onValuesChange={(_, allValues) => {
            this.setState({ programacionEvaluaciones: allValues.programacionEvaluaciones || [] });
          }}
        >
          <Form.List name="programacionEvaluaciones">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => {
                  const fila = programacionEvaluaciones?.[name];
                  const tieneAncla = !!fila?.ultimaGenerada;
                  const tieneInicioForzado = !tieneAncla && !!fila?.inicioProgramado;
                  const diaDerivadoDe = tieneAncla
                    ? fila.ultimaGenerada
                    : tieneInicioForzado
                      ? fila.inicioProgramado
                      : null;
                  return (
                    <div
                      key={key}
                      style={{
                        border: "1px solid #ebedf0",
                        borderRadius: 6,
                        padding: "12px 14px",
                        marginBottom: 12,
                        background: "#fafafa",
                      }}
                    >
                      <Row gutter={[12, 12]}>
                        <Col span={8}>
                          <FormItem
                            {...restField}
                            name={[name, "plantillaId"]}
                            label="Plantilla"
                            rules={[{ required: true, message: "Elige una plantilla" }]}
                            {...FULL}
                            tooltip="Solo se listan plantillas autorizadas. Si el proveedor tiene Tipo de Proveedor asignado, se filtran a ese tipo (o sin tipo específico)."
                          >
                            <SelectPlantillasEvaluacionItem
                              headersToken={this.props.headersToken}
                              empresaId={this.props.empresaId}
                              tipoProveedorId={tipoProveedorId}
                            />
                          </FormItem>
                        </Col>
                        <Col span={4}>
                          {diaDerivadoDe ? (
                            <FormItem label="Día del mes" {...FULL}>
                              <InputNumberItem
                                disabled
                                style={{ width: "100%" }}
                                value={moment(diaDerivadoDe).date()}
                              />
                            </FormItem>
                          ) : (
                            <FormItem
                              {...restField}
                              name={[name, "dia"]}
                              label="Día del mes"
                              rules={[{ required: true, message: "Requerido" }]}
                              {...FULL}
                              tooltip="Del 1 al 31. Si el mes no tiene ese día (ej. 31 en febrero), se recorta al último día del mes."
                            >
                              <InputNumberItem min={1} max={31} style={{ width: "100%" }} />
                            </FormItem>
                          )}
                        </Col>
                        <Col span={5}>
                          <FormItem
                            {...restField}
                            name={[name, "frecuencia"]}
                            label="Frecuencia"
                            rules={[{ required: true, message: "Requerido" }]}
                            {...FULL}
                          >
                            <SelectItem options={FRECUENCIAS} style={{ width: "100%" }} />
                          </FormItem>
                        </Col>
                        <Col span={5}>
                          <FormItem
                            {...restField}
                            name={[name, "usuarioNotificarId"]}
                            label="Notificar a"
                            {...FULL}
                          >
                            <SelectItem allowClear style={{ width: "100%" }} placeholder="Nadie">
                              {usuariosEmpresa.map((u) => (
                                <Select.Option key={u._id} value={u._id}>
                                  {`${u.nombre} ${u.apellidoPaterno ?? ""} ${u.apellidoMaterno ?? ""}`.trim()}
                                </Select.Option>
                              ))}
                            </SelectItem>
                          </FormItem>
                        </Col>
                        <Col span={2} style={{ textAlign: "right", paddingTop: 28 }}>
                          <ButtonItem
                            danger
                            size="small"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(name)}
                            tooltip="Eliminar esta programación"
                          />
                        </Col>
                      </Row>
                      <Row gutter={[12, 12]} align="middle">
                        <Col span={8}>
                          <FormItem
                            {...restField}
                            name={[name, "ultimaGenerada"]}
                            label="Última evaluación realizada"
                            {...FULL}
                            tooltip="Solo si a este proveedor ya le habían hecho esta evaluación antes de programarla aquí (en papel, en otro sistema, etc). El día del mes se toma de esta fecha y la próxima fecha se cuenta a partir de ella, no de hoy, para no romper la cadencia real. Deja esto y lo de abajo vacíos si es la primera vez que se programa (ahí sí pide el día del mes)."
                          >
                            <DatePickerItem style={{ width: "100%" }} />
                          </FormItem>
                        </Col>
                        <Col span={8}>
                          <FormItem
                            {...restField}
                            name={[name, "inicioProgramado"]}
                            label="Forzar primera evaluación para"
                            {...FULL}
                            tooltip={
                              tieneAncla
                                ? "No aplica: ya capturaste 'Última evaluación realizada' arriba, esa manda."
                                : 'Para cuando NO ha habido ninguna evaluación previa pero no quieres que la primera salga apenas se calcule desde hoy. Ej: hoy es febrero, la frecuencia es trimestral y el día 3 — sin esto la primera caería en mayo (el 3 de febrero ya pasó); si pones aquí 03/04, la primera será hasta abril y de ahí en adelante seguirá cada 3 meses.'
                            }
                          >
                            <DatePickerItem disabled={tieneAncla} style={{ width: "100%" }} />
                          </FormItem>
                        </Col>
                        <Col span={8}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {fila?.proximaFecha
                              ? <>Próxima evaluación: <Tag>{moment(fila.proximaFecha).format("DD/MM/YYYY")}</Tag></>
                              : "La próxima fecha se calcula al guardar."}
                          </Text>
                        </Col>
                      </Row>
                    </div>
                  );
                })}

                <ButtonItem
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  onClick={() => add({ dia: 1, frecuencia: "trimestral" })}
                  style={{ marginBottom: 16 }}
                >
                  Añadir Programación
                </ButtonItem>
              </>
            )}
          </Form.List>

          <div style={{ textAlign: "right" }}>
            <ButtonItem verde onClick={this.guardar} loading={this.state.guardando}>
              Guardar Programación
            </ButtonItem>
          </div>
        </Form>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ProgramacionEvaluacionesProveedor);
